import React, {useState} from 'react';
import {AUDIO_ACCEPT} from '../../lib/browserFiles';
import {formatBytes} from '../../lib/audioMetadata';
import {
  PickedAudioFile,
  pickAudioFilesWithHandles,
  pickedFilesFromDrop,
  SHOW_PICKER_AUDIO_TYPES,
} from '../../lib/fileHandles';
import {UploadCloudIcon, XIcon} from '../Icons';

export interface DropzoneUploadState {
  /** Name of the file currently going up. */
  fileName: string;
  /** What the upload is doing right now, already phrased for the user. */
  phaseLabel: string;
  /** Only meaningful while bytes are moving; -1 hides the bar. */
  percent: number;
  loaded: number;
  total: number;
  /** `2 of 5` when several files were dropped at once, empty for a single file. */
  positionLabel: string;
}

interface UploadDropzoneProps {
  onFilesPicked: (files: PickedAudioFile[]) => void;
  onCancel: () => void;
  upload: DropzoneUploadState | null;
  /** Blocks picking while something is already going up — uploads run one at a time. */
  disabled?: boolean;
  /** Shown under the headline, e.g. the plan's real ceiling. */
  limitsLabel: string;
}

/**
 * Drop target and the upload's progress, in one place.
 *
 * The desktop app made choosing a file a button and the upload a separate card below it, which is
 * two steps for what is one gesture on the web. Dropping a file *is* the upload here; the zone
 * turns into the progress readout for the same file rather than handing it off to another panel.
 *
 * Both ways in try to come away with a `FileSystemFileHandle` as well as the `File`, because an
 * upload that drops can now be resumed and a stored handle is what lets Resume re-open the same
 * file with no second trip to the picker. Only Chromium has one; everywhere else the plain `File`
 * is all there is, which costs a click on resume and nothing else.
 */
const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  onFilesPicked,
  onCancel,
  upload,
  disabled = false,
  limitsLabel,
}) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDraggingOver(false);

    if (disabled) {
      return;
    }

    // `pickedFilesFromDrop` reads everything off the DataTransfer synchronously — the item list is
    // neutered once this handler returns, so it cannot be awaited first.
    void pickedFilesFromDrop(event.dataTransfer).then((picked) => {
      if (picked.length > 0) {
        onFilesPicked(picked);
      }
    });
  };

  // Prefers the picker that hands back handles, and falls back to the hidden input — which is also
  // what the header's "Upload audio" button reaches for, so the input has to stay in the DOM.
  const handleBrowseClick = async () => {
    const picked = await pickAudioFilesWithHandles(SHOW_PICKER_AUDIO_TYPES, true);

    if (picked === null) {
      inputRef.current?.click();
      return;
    }
    if (picked.length > 0) {
      onFilesPicked(picked);
    }
  };

  if (upload) {
    return (
      <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/[0.04] p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-100 truncate">{upload.fileName}</p>
            <p className="mt-0.5 text-xs text-indigo-300">
              {upload.phaseLabel}
              {upload.positionLabel && <span className="text-gray-500"> · {upload.positionLabel}</span>}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-300 transition-colors cursor-pointer"
          >
            <XIcon className="w-3.5 h-3.5" />
            Cancel
          </button>
        </div>

        {upload.percent >= 0 && (
          <>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-800">
              <div
                className="h-full rounded-full bg-indigo-500 transition-[width] duration-200"
                style={{width: `${upload.percent}%`}}
              />
            </div>
            <div className="mt-1.5 flex justify-between text-xs tabular-nums text-gray-500">
              <span>{upload.percent}%</span>
              {upload.total > 0 && (
                <span>{formatBytes(upload.loaded)} of {formatBytes(upload.total)}</span>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) {
          setIsDraggingOver(true);
        }
      }}
      onDragLeave={() => setIsDraggingOver(false)}
      onDrop={handleDrop}
      className={`rounded-xl border border-dashed transition-colors ${
        isDraggingOver
          ? 'border-indigo-500 bg-indigo-500/[0.06]'
          : 'border-gray-800 bg-gray-900/40 hover:border-gray-700'
      }`}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => { void handleBrowseClick(); }}
        className="flex w-full flex-col items-center gap-1 px-6 py-8 text-center
         disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
      >
        <UploadCloudIcon className={`w-6 h-6 mb-1 ${isDraggingOver ? 'text-indigo-400' : 'text-gray-600'}`} />
        <span className="text-sm font-medium text-gray-200">
          Drop audio files here or click to upload
        </span>
        <span className="text-xs text-gray-500">{limitsLabel}</span>
        <span className="text-xs text-gray-600">
          Files upload to the cloud.
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        accept={AUDIO_ACCEPT}
        multiple
        className="hidden"
        onChange={(event) => {
          const files = event.target.files ? Array.from(event.target.files) : [];
          // Reset, or picking the same file twice in a row fires no change event.
          event.target.value = '';
          if (files.length > 0) {
            // No handles from an <input>; resume will ask for the file again.
            onFilesPicked(files.map(file => ({file})));
          }
        }}
      />
    </div>
  );
};

export default UploadDropzone;
